import React from 'react';
import PropTypes from 'prop-types';
import { ActivityEventLine, DocumentIconPicker } from '@/components/common';
import Link from '@/components/common/Link';
import { formatDate, formatTime } from '@/util';
import { openDrawer } from '@/state/actions';
import store from '@/state/store';
import { getDiscountById } from '@/modules/dataWrangler/discounts';
import { getProductById } from '@/modules/dataWrangler/products';
import { getTagById } from '@/modules/dataWrangler/tags';
import { getTaxById } from '@/modules/dataWrangler/taxes';

import useStyles from './styles';

// Process array of objects into a stripped down version
// that only has required data so is faster to process.
export const dataProcessor = (dataArray) =>
    dataArray.map(
        ({
            activityId,
            data,
            activityType,
            createdAt,
            parentType,
            parentLabel,
            parentId
        }) => ({
            id: activityId,
            paymentMethod: data?.method || '',
            activityType,
            createdAt,
            parentType,
            parentLabel,
            parentId
        })
    );

const openDrawerHandler = async ({ type, id }) => {
    const allowedTypes = ['discount', 'product', 'service', 'tag', 'tax'];

    if (!allowedTypes.includes(type)) return;

    let data = null;
    let title = '';

    if (type === 'discount') {
        data = await getDiscountById(id);
        title = 'Edit Discount';
    } else if (type === 'product' || type === 'service') {
        data = await getProductById(id);

        title = `Edit ${type.replace(/\b\w/g, (l) => l.toUpperCase())}`;
    } else if (type === 'tag') {
        data = await getTagById(id);
        title = 'Edit Tag';
    } else if (type === 'tax') {
        data = await getTaxById(id);
        title = 'Edit Tax';
    }

    if (!data) return;

    store.dispatch(
        openDrawer({
            // Set the key dynamically
            [`${type}`]: data,
            id: type,
            edit: true,
            title,
            updating: true
        })
    );
};

const ItemLink = ({ parentType, parentLabel, parentId }) => {
    const classes = useStyles();

    const drawerType = ['discount', 'product', 'service', 'tag', 'tax'];

    if (drawerType.includes(parentType)) {
        return (
            <span className={classes.lineCenter}>
                <DocumentIconPicker type={parentType} />

                <button
                    type="button"
                    className={classes.simpleButton}
                    onClick={() =>
                        openDrawerHandler({
                            type: parentType,
                            id: parentId
                        })
                    }
                >
                    {parentLabel}
                </button>
            </span>
        );
    }

    let pagelink = `/${parentType}/${parentId}`;
    if (parentType === 'client') {
        pagelink = `/clients/${parentId}`;
    }

    return (
        <span className={classes.lineCenter}>
            <DocumentIconPicker type={parentType} />

            <Link to={pagelink} className={classes.lineLabel}>
                {parentLabel}
            </Link>
        </span>
    );
};

ItemLink.propTypes = {
    parentType: PropTypes.string.isRequired,
    parentLabel: PropTypes.string.isRequired,
    parentId: PropTypes.string.isRequired
};

export const generateComponents = ({ array, locale, classes }) =>
    array.map(
        ({
            activityType,
            createdAt,
            parentType,
            parentLabel,
            parentId,
            paymentMethod,
            id
        }) => ({
            id,
            event: (
                <ActivityEventLine
                    activityType={activityType}
                    parentType={parentType}
                    method={paymentMethod}
                />
            ),
            // user: <span className={classes.line}>John Doe</span>,
            itemid: (
                <ItemLink
                    parentType={parentType}
                    parentLabel={parentLabel}
                    parentId={parentId}
                />
            ),
            date: (
                <span className={classes.lineDate}>
                    <span>
                        {formatDate({
                            isoDate: createdAt,
                            locale
                        })}
                    </span>{' '}
                    <span className={classes.lineDateTime}>
                        {formatTime({
                            isoDate: createdAt,
                            locale
                        })}
                    </span>
                </span>
            )
        })
    );
